import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../../services/auth.service';
import {
    fadeInLeftOnEnterAnimation,
    fadeInOnEnterAnimation,
    fadeInUpBigOnEnterAnimation,
    fadeOutOnLeaveAnimation,
    fadeOutRightOnLeaveAnimation,
    headShakeAnimation,
} from 'angular-animations';
import {Subscription} from 'rxjs';
import {EventsService} from '../../../services/events.service';
import {environment} from '../../../../environments/environment';
import {AnalyticsService} from '../../../services/analytics.service';
import {CoachService} from '../service/coach.service';
import {RequesterResponseModel} from '../../../services/models/requester.model';
import {UserService} from '../../../services/user.service';
import {NavService} from '../../../services/nav.service';
import {AlertController, LoadingController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';
import {CoachSessionModel} from '../models/session.model';

@Component({
    selector: 'app-coach-feedback-page',
    templateUrl: 'coach-feedback.page.html',
    styleUrls: ['coach-feedback.page.scss'],
    animations: [
        fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
        fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
        fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
        fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
        fadeInUpBigOnEnterAnimation({anchor: 'enterUp', duration: 300}),
        headShakeAnimation(),
    ],
})
export class CoachFeedbackPage implements OnInit, OnDestroy {
    subscriptions: Subscription[] = [];

    environment = null;
    modalIsVisible = false;

    public animationState = false;
    platform = 'website';

    section = 'start'; // start, question, end
    currentQuestion = 0;

    userType = null;
    level = 'learner';
    sessionId: number = null;

    isTitle = true;

    session: CoachSessionModel = null;
    sessionPromise: Promise<CoachSessionModel> = null;
    sessionValid = false;

    questions = [
        {
            title: '1. The coach demonstrated a good knowledge of the subject:',
            answer: '',
        },
        {
            title: '2. The coach\'s communication was clear, concise, and engaging and they listened actively and attentively:',
            answer: '',
        },
        {
            title: '3. The coaching from this meeting will help me achieve my existing goals and objectives:',
            answer: '',
        },
        {
            title: '4. The coach recommended further goals and objectives that will help me in my development against the company Competency Framework:',
            answer: '',
        },
        {
            title: '5. I would recommend this coach to others:',
            answer: '',
        },
    ];

    constructor(
        private event: EventsService,
        private activatedRoute: ActivatedRoute,
        public authService: AuthService,
        public userService: UserService,
        private coachService: CoachService,
        private analytics: AnalyticsService,
        private nav: NavService,
        private alertController: AlertController,
        private translate: TranslateService,
        private loadingCtrl: LoadingController
    ) {
        this.environment = environment;
        this.subscriptions.push(
            this.event.subscribe('login', () => {
                this.sessionValid = false;
            })
        );
        this.subscriptions.push(
            this.event.subscribe('logout', () => {
                this.sessionValid = false;
            })
        );
    }

    ngOnInit(): void {
        this.platform = environment.platform;
        this.sessionId = Number(this.activatedRoute.snapshot.paramMap.get('sessionId'));
        this.level = this.activatedRoute.snapshot.data.level;

        console.log('sessionId', this.sessionId)
        console.log('level', this.level)
    }

    ionViewWillEnter() {
        this.userType = this.authService.getUserType();

        if (this.session === null || !this.sessionValid) {
            this.sessionPromise = this.coachService.getCoachSessionInfo(this.sessionId);
        }
    }

    ionViewDidEnter() {
        this.analytics.trackView('Your Coaching Journey');

        if (this.session === null || !this.sessionValid) {
            this.sessionPromise.then(data => {
                this.getCoachSessionInfoHandler(data);
            });
        }
    }

    getCoachSessionInfoHandler(data) {
        if (data === null) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_ERROR_TITLE__'),
                message: this.translate.instant('__ALERT_ERROR_EMPTY_DATA_MESSAGE__'),
                buttons: [
                    {
                        text: this.translate.instant('__ALERT_ERROR_BUTTON__'),
                        handler: () => {
                            console.log('Back')
                            this.goBack();
                        }
                    }]
            }).then(a => a.present());
        } else {
            this.session = data;
            this.sessionValid = true;
        }
    }

    async showLoading() {
        const loading = await this.loadingCtrl.create({
            message: 'Loading...',
            duration: 1000,
            spinner: 'circles',
        });

        loading.present();
    }

    displayErrorAlert(message: string, header: string = 'default') {
        this.alertController.create({
            header: header === 'default' ? this.translate.instant('__ALERT_ERROR_TITLE__') : header,
            message: this.translate.instant(message),
            buttons: [this.translate.instant('__ALERT_ERROR_BUTTON__')]
        }).then(a => a.present());
    }

    displaySuccessAlert(message: string) {
        this.alertController.create({
            header: this.translate.instant('__ALERT_SUCCESS_TITLE__'),
            message: this.translate.instant(message),
            buttons: [this.translate.instant('__ALERT_SUCCESS_BUTTON__')]
        }).then(a => a.present());
    }

    start() {
        this.section = 'question';
    }

    answer(value) {
        this.questions[this.currentQuestion].answer = value;
        if (this.currentQuestion === this.questions.length - 1) {
            this.saveAnswers().then(() => {
                this.section = 'end';
            })
        } else {
            this.isTitle = false;
            this.currentQuestion++;
            setTimeout(() => {
                this.isTitle = true;
            }, 500);
        }
    }

    saveAnswers() {
        this.showLoading();

        const feedback = this.questions
            .reduce((acc, item, index) => [...acc, item.answer], [])
            .join(',');

        return this.coachService.saveMyCoachSessionFeedback(this.sessionId, feedback).then(data => {
            if (data.hasOwnProperty('status') && data['status'] === 'ok') {
                this.event.publish('form:saved', {name: 'coaching-feedback'});
                // this.displaySuccessAlert((data as RequesterResponseModel).message);
                return true;
            } else {
                this.displayErrorAlert('Something went wrong, please try again later.');
                return false;
            }
        }).catch(() => {
            this.displayErrorAlert('Something went wrong, please try again later.');
            return false;
        });
    }

    previous() {
        if (this.currentQuestion > 0) {
            this.isTitle = false;
            this.currentQuestion--;
            setTimeout(() => {
                this.isTitle = true;
            }, 500);
        }
    }

    goBack() {
        this.nav.back();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((i) => i.unsubscribe());
    }
}
